import type {
  EnviarMensajeInput,
  FiltroBandeja,
  LeadActualizarInput,
  LeadBloquearInput,
  LeadCalificarInput,
  LeadCerrarInput,
  LeadPublic,
  MensajePublic,
  SincronizarInput,
  SincronizarResultado,
} from '@app/shared';

import { api } from '../../../lib/api';

/** Extensión de archivo según el mimetype que entrega MediaRecorder (webm en Chrome). */
function extensionDeAudio(mimeType: string): string {
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('webm')) return 'webm';
  return 'bin';
}

export interface LeadsListResponse {
  leads: LeadPublic[];
  conteos: Record<FiltroBandeja, number>;
}

export const leadsApi = {
  async list(params: {
    filtro: FiltroBandeja;
    buscar?: string;
    campana?: string;
    etapa?: number;
  }): Promise<LeadsListResponse> {
    const { data } = await api.get<LeadsListResponse>('/leads', {
      params: {
        filtro: params.filtro,
        ...(params.buscar ? { buscar: params.buscar } : {}),
        ...(params.campana ? { campana: params.campana } : {}),
        ...(params.etapa ? { etapa: params.etapa } : {}),
      },
    });
    return data;
  },

  async get(id: string): Promise<{ lead: LeadPublic }> {
    const { data } = await api.get<{ lead: LeadPublic }>(`/leads/${id}`);
    return data;
  },

  async mensajes(id: string): Promise<{ mensajes: MensajePublic[] }> {
    const { data } = await api.get<{ mensajes: MensajePublic[] }>(`/leads/${id}/mensajes`);
    return data;
  },

  async marcarLeido(id: string): Promise<void> {
    await api.post(`/leads/${id}/leido`);
  },

  async enviar(id: string, input: EnviarMensajeInput): Promise<{ mensaje: MensajePublic }> {
    const { data } = await api.post<{ mensaje: MensajePublic }>(`/leads/${id}/enviar`, input);
    return data;
  },

  /** Ciclo audio, R3: manda el audio grabado en el compositor. */
  async enviarAudio(
    id: string,
    blob: Blob,
    meta: { instancia?: string; fijarComoPreferida?: boolean },
  ): Promise<{ mensaje: MensajePublic }> {
    const form = new FormData();
    form.append('audio', blob, `audio.${extensionDeAudio(blob.type)}`);
    if (meta.instancia) form.append('instancia', meta.instancia);
    if (meta.fijarComoPreferida !== undefined) {
      form.append('fijarComoPreferida', String(meta.fijarComoPreferida));
    }
    const { data } = await api.post<{ mensaje: MensajePublic }>(`/leads/${id}/enviar-audio`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  async cerrar(id: string, input: LeadCerrarInput): Promise<{ lead: LeadPublic }> {
    const { data } = await api.post<{ lead: LeadPublic }>(`/leads/${id}/cerrar`, input);
    return data;
  },

  /** Eliminar (borrado lógico, motivo obligatorio). */
  async bloquear(id: string, input: LeadBloquearInput): Promise<{ lead: LeadPublic }> {
    const { data } = await api.post<{ lead: LeadPublic }>(`/leads/${id}/bloquear`, input);
    return data;
  },

  /** ciclo-calidad-leads: Bueno / Basura (con motivo) / Compró, desde el ícono de la charla. */
  async calificar(id: string, input: LeadCalificarInput): Promise<{ lead: LeadPublic }> {
    const { data } = await api.post<{ lead: LeadPublic }>(`/leads/${id}/calificar`, input);
    return data;
  },

  async actualizar(id: string, input: LeadActualizarInput): Promise<{ lead: LeadPublic }> {
    const { data } = await api.patch<{ lead: LeadPublic }>(`/leads/${id}`, input);
    return data;
  },

  /** Respaldo (últimas páginas) o historial (todo), de UNA instancia (Ciclo 1b, R4). */
  async sincronizar(input: Partial<SincronizarInput> & { instancia: string }): Promise<SincronizarResultado> {
    const { data } = await api.post<SincronizarResultado>('/leads/sincronizar', input);
    return data;
  },
};
